import React, { Fragment, useState, useEffect, useRef } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";

import { Submenu } from "../../typings/Submenu";
import { MenuItem } from "./MenuItem";
import { useUIMode } from "../../wms/src/context/UIContext";

interface MultilevelProps {
  item: Submenu;
  handleSubitemClick: (item: Submenu) => void;
  selectedItem?: string | null;
  onSelectItem?: (id: string) => void;
  forceExpanded?: boolean;
}

export const MultiLevel: React.FC<MultilevelProps> = ({
  item,
  handleSubitemClick,
  selectedItem,
  onSelectItem,
  forceExpanded
}) => {
  const { items } = item;
  const { isShortMode, isCollapsed: collapsedMode } = useUIMode();
  const isCollapsed = collapsedMode && !forceExpanded;
  const [open, setOpen] = useState(isShortMode);

  // Flyout (panel flotante) cuando el menú está colapsado
  const anchorRef = useRef<HTMLDivElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

   useEffect(() => {
    if (isShortMode) {
      setOpen(true);
    }
    else{
      setOpen(false);
    }
  }, [isShortMode]);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const renderChildren = () =>
    items?.map((child: Submenu, key) => (
      <MenuItem
        key={key}
        submenu={child}
        handleSubitemClick={() => handleSubitemClick(child)}
        selectedItem={selectedItem}
        onSelectItem={onSelectItem}
        forceExpanded={forceExpanded}
      />
    ));

  const IconComponent = item.icon;

  // Modo colapsado: solo icono + flyout en hover con los hijos
  if (isCollapsed) {
    return (
      <Fragment>
        <div
          ref={anchorRef}
          onMouseEnter={() => setFlyoutOpen(true)}
          onMouseLeave={() => setFlyoutOpen(false)}
        >
          <Tooltip title={flyoutOpen ? "" : item.name} placement="right">
            <ListItemButton
              selected={selectedItem === item.url}
              sx={{ justifyContent: "center", px: 0, py: 1.5 }}
            >
              {IconComponent && (
                <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                  {React.createElement(IconComponent as any, {
                    sx: { fontSize: "22px" },
                  })}
                </ListItemIcon>
              )}
            </ListItemButton>
          </Tooltip>
          <Popper
            open={flyoutOpen}
            anchorEl={anchorRef.current}
            placement="right-start"
            style={{ zIndex: 1300 }}
          >
            <Paper elevation={4} sx={{ minWidth: 220, py: 0.5 }}>
              <Typography
                sx={{ px: 2, py: 1, fontWeight: 700, fontSize: "14px" }}
              >
                {item.name}
              </Typography>
              <List component="div" disablePadding>
                {/* Forzamos render expandido dentro del flyout */}
                {items?.map((child: Submenu, key) => (
                  <MenuItem
                    key={key}
                    submenu={child}
                    handleSubitemClick={() => handleSubitemClick(child)}
                    selectedItem={selectedItem}
                    onSelectItem={onSelectItem}
                    forceExpanded={true}
                  />
                ))}
              </List>
            </Paper>
          </Popper>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <ListItemButton
        onClick={handleClick}
        selected={selectedItem === item.url}
        sx={{ pl: 2, py: isShortMode ? 2.5 : 1 }}
      >
        {IconComponent && (
          <ListItemIcon sx={{ minWidth: 36 }}>
            {React.createElement(IconComponent as any, {
              sx: { fontSize: isShortMode ? "26px" : "20px" },
            })}
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{
            style: { fontWeight: 900 },
            sx: {
              // Letra más grande y en negrita si está en modo móvil
              fontSize: isShortMode ? "19px" : "16px",
              fontWeight: isShortMode ? 1200 : 900,
            },
          }}
        />
        {!isShortMode && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
      </ListItemButton>
      <Collapse in={open} timeout="auto">
        <List component="div" disablePadding>
          {renderChildren()}
        </List>
      </Collapse>
    </Fragment>
  );
};
